import React, { useState, useEffect } from "react";

type UnitType = 'length' | 'weight' | 'temperature' | 'volume' | 'data';

interface UnitOption {
    value: string;
    label: string;
    conversion: (value: number, reverse?: boolean) => number;
}

interface UnitCategory {
    name: string;
    units: UnitOption[];
}

const unitCategories: Record<UnitType, UnitCategory> = {
    length: {
        name: "Length",
        units: [
            { 
                value: "meters", 
                label: "Meters (m)", 
                conversion: (value: number) => value 
            },
            { 
                value: "feet", 
                label: "Feet (ft)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value / 3.28084 : value * 3.28084 
            },
            { 
                value: "inches", 
                label: "Inches (in)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value / 39.3701 : value * 39.3701 
            },
            { 
                value: "kilometers", 
                label: "Kilometers (km)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1000 : value / 1000 
            },
            { 
                value: "miles", 
                label: "Miles (mi)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1609.34 : value / 1609.34 
            }
        ]
    },
    weight: {
        name: "Weight",
        units: [
            { 
                value: "kilograms", 
                label: "Kilograms (kg)", 
                conversion: (value: number) => value 
            },
            { 
                value: "pounds", 
                label: "Pounds (lb)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value / 2.20462 : value * 2.20462 
            },
            { 
                value: "ounces", 
                label: "Ounces (oz)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value / 35.274 : value * 35.274 
            },
            { 
                value: "grams", 
                label: "Grams (g)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value / 1000 : value * 1000 
            }
        ]
    },
    temperature: {
        name: "Temperature",
        units: [
            { 
                value: "celsius", 
                label: "Celsius (°C)", 
                conversion: (value: number) => value 
            },
            { 
                value: "fahrenheit", 
                label: "Fahrenheit (°F)", 
                conversion: (value: number, reverse?: boolean) => reverse ? (value - 32) * 5/9 : (value * 9/5) + 32 
            },
            { 
                value: "kelvin", 
                label: "Kelvin (K)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value - 273.15 : value + 273.15 
            }
        ]
    },
    volume: {
        name: "Volume",
        units: [
            { 
                value: "liters", 
                label: "Liters (L)", 
                conversion: (value: number) => value 
            },
            { 
                value: "gallons", 
                label: "Gallons (gal)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value * 3.78541 : value / 3.78541 
            },
            { 
                value: "milliliters", 
                label: "Milliliters (mL)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value / 1000 : value * 1000 
            },
            { 
                value: "cubic_meters", 
                label: "Cubic Meters (m³)", 
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1000 : value / 1000 
            }
        ]
    },
    data: {
        name: "Data Storage",
        units: [
            {
                value: "bytes",
                label: "Bytes (B)",
                conversion: (value: number) => value
            },
            {
                value: "kilobytes",
                label: "Kilobytes (KB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 : value / 1024
            },
            {
                value: "megabytes",
                label: "Megabytes (MB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 * 1024 : value / (1024 * 1024)
            },
            {
                value: "gigabytes",
                label: "Gigabytes (GB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 * 1024 * 1024 : value / (1024 * 1024 * 1024)
            },
            {
                value: "terabytes",
                label: "Terabytes (TB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 * 1024 * 1024 * 1024 : value / (1024 * 1024 * 1024 * 1024)
            },
            {
                value: "bits",
                label: "Bits (b)",
                conversion: (value: number, reverse?: boolean) => reverse ? value / 8 : value * 8
            },
            {
                value: "kibibytes",
                label: "Kibibytes (KiB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 : value / 1024
            },
            {
                value: "mebibytes",
                label: "Mebibytes (MiB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 * 1024 : value / (1024 * 1024)
            },
            {
                value: "gibibytes",
                label: "Gibibytes (GiB)",
                conversion: (value: number, reverse?: boolean) => reverse ? value * 1024 * 1024 * 1024 : value / (1024 * 1024 * 1024)
            }
        ]
    }
};

function UnitConverter() {
    const [unitType, setUnitType] = useState<UnitType>('length');
    const [fromUnit, setFromUnit] = useState<string>(unitCategories[unitType].units[0].value);
    const [toUnit, setToUnit] = useState<string>(unitCategories[unitType].units[1].value);
    const [inputValue, setInputValue] = useState<string>('');
    const [result, setResult] = useState<string>('');

    // Reset units when unit type changes
    useEffect(() => {
        setFromUnit(unitCategories[unitType].units[0].value);
        setToUnit(unitCategories[unitType].units[1].value);
        setResult('');
    }, [unitType]);

    // Convert value when any of the conversion parameters change
    useEffect(() => {
        convertValue();
    }, [fromUnit, toUnit, inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const convertValue = () => {
        if (!inputValue) {
            setResult('');
            return;
        }

        const value = parseFloat(inputValue);
        if (isNaN(value)) {
            setResult('Invalid input');
            return;
        }

        const fromUnitObj = unitCategories[unitType].units.find(u => u.value === fromUnit);
        const toUnitObj = unitCategories[unitType].units.find(u => u.value === toUnit);

        if (!fromUnitObj || !toUnitObj) return;

        // Convert to base unit first (meters, kilograms, celsius, liters)
        const baseValue = fromUnitObj.conversion(value, true);
        // Then convert to target unit
        const finalValue = toUnitObj.conversion(baseValue);

        setResult(finalValue.toFixed(4));
    };

    return (
        <div className="tool">
            <h2>Unit Converter</h2>
            <div className="content">
                <div className="unit-converter">
                    <div className="unit-converter__type-selector">
                        {Object.entries(unitCategories).map(([type, category]) => (
                            <button
                                key={type}
                                className={`unit-converter__type-button ${unitType === type ? 'active' : ''}`}
                                onClick={() => setUnitType(type as UnitType)}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>

                    <div className="unit-converter__conversion-container">
                        <div className="unit-converter__input-group">
                            <input
                                type="number"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder="Enter value"
                                className="unit-converter__input"
                            />
                            <select
                                value={fromUnit}
                                onChange={(e) => setFromUnit(e.target.value)}
                                className="unit-converter__select"
                            >
                                {unitCategories[unitType].units.map(unit => (
                                    <option key={unit.value} value={unit.value}>
                                        {unit.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="unit-converter__equals">=</div>

                        <div className="unit-converter__input-group">
                            <input
                                type="text"
                                value={result}
                                readOnly
                                className="unit-converter__input"
                                placeholder="Result"
                            />
                            <select
                                value={toUnit}
                                onChange={(e) => setToUnit(e.target.value)}
                                className="unit-converter__select"
                            >
                                {unitCategories[unitType].units.map(unit => (
                                    <option key={unit.value} value={unit.value}>
                                        {unit.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UnitConverter;